import { Injectable } from '@angular/core';
import { deepEqualInAnyOrder, stableStringify } from '../../../utils/utils';
import { convertToWorkflow } from '../../../utils/workflow/workflow-misc-utils';
import { StepType } from '../enums/step-type.enum';
import { WorkflowV1Run } from '../models';
import { FileModel } from '../models/file-model.model';
import { ListResult } from '../models/list-result';
import { ModuleCategoryV1Read } from '../models/module-category-v1-read.model';
import { ModuleComplianceV1Read } from '../models/module-compliance-v1-read.model';
import { ModuleV1Read } from '../models/module-v1-read.model';
import { Result } from '../models/result.model';
import { RunSearchOptions } from '../models/run-search-options.model';
import { Step } from '../models/step.model';
import { Workflow } from '../models/workflow.model';

export interface ModuleFiles {
    flowId: string;
    inputFiles: string[];
}

@Injectable({
    providedIn: 'root',
})
export class WorkflowStateService {
    private _workflow: Workflow | null;
    private _systemModules: ModuleV1Read[] | null = null;
    private _allVisibleModules: ModuleV1Read[] | null = null;
    private _experiments: ListResult<Result> | null = null;
    private _allModuleCompliance: ModuleComplianceV1Read[] | null = null;
    private _allModuleCategories: ModuleCategoryV1Read[] | null = null;
    private _isPublicWorkflowUpdateByAdmin: boolean = false;
    private _originalWorkflowJSON: string | undefined = undefined;
    // this is sort object per flowId since we can have workflow input and multiple module inputs where we can have a list of files
    private _runSearchObject: { [id: string]: RunSearchOptions } = {};
    // #341058 This(_previousArivisViewerImage, _rerun) is dirty fix for arivis request being sent twice which
    // happens because components are being destroyed and recreated for every clone.
    // This will get resolved once we refactor cloning run steps when reporting status and changing parameters
    // according to #342183.
    private _previousArivisViewerImage: string | FileModel | undefined = undefined;
    private _rerun: boolean = false;
    private _initialFiles: ModuleFiles[] = [];

    set workflow(workflow: Workflow | null) {
        this._workflow = workflow;
        if (!this._originalWorkflowJSON) {
            this.setCurrentWorkflowAsOriginal();
        }
        if (workflow?.visibility === 'Public' && this.initialFiles.length === 0) {
            this._initialFiles = workflow.steps.reduce((moduleFiles: ModuleFiles[], step: Step) => {
                if (step.type === StepType.WorkflowDefineInput || step.type === StepType.ModuleDefineInput) {
                    for (const inputParameter of step.inputParameters) {
                        const inputParameterValue = inputParameter.getValue();
                        if (inputParameterValue) {
                            moduleFiles.push({
                                flowId: step.flowId,
                                inputFiles: Array.isArray(inputParameterValue) ? inputParameterValue : [inputParameterValue],
                            } as ModuleFiles);
                        }
                    }
                }
                return moduleFiles;
            }, []);
        }
    }

    get workflow(): Workflow | null {
        return this._workflow;
    }

    get isPublic(): boolean {
        return this._workflow?.visibility === 'Public';
    }

    get initialFiles(): ModuleFiles[] {
        return this._initialFiles;
    }

    set systemModules(systemModules: ModuleV1Read[] | null) {
        this._systemModules = systemModules;
    }

    get systemModules(): ModuleV1Read[] | null {
        return this._systemModules;
    }

    set allVisibleModules(allVisibleModules: ModuleV1Read[] | null) {
        this._allVisibleModules = allVisibleModules;
    }

    get allVisibleModules(): ModuleV1Read[] | null {
        return this._allVisibleModules;
    }

    set allModuleCompliance(allModuleCompliance: ModuleComplianceV1Read[] | null) {
        this._allModuleCompliance = allModuleCompliance;
    }

    get allModuleCompliance(): ModuleComplianceV1Read[] | null {
        return this._allModuleCompliance;
    }

    set allModuleCategories(allModuleCategories: ModuleCategoryV1Read[] | null) {
        this._allModuleCategories = allModuleCategories;
    }

    get allModuleCategories(): ModuleCategoryV1Read[] | null {
        return this._allModuleCategories;
    }

    set experiments(experiments: ListResult<Result> | null) {
        this._experiments = experiments;
    }

    get experiments(): ListResult<Result> | null {
        return this._experiments;
    }

    set isPublicWorkflowUpdateByAdmin(isPublicWorkflowUpdateByAdmin: boolean) {
        this._isPublicWorkflowUpdateByAdmin = isPublicWorkflowUpdateByAdmin;
    }

    get isPublicWorkflowUpdateByAdmin(): boolean {
        return this._isPublicWorkflowUpdateByAdmin;
    }

    get runSearchObject(): { [id: string]: RunSearchOptions } {
        return this._runSearchObject;
    }
    get previousArivisViewerImage(): string | FileModel | undefined {
        return this._previousArivisViewerImage;
    }

    set previousArivisViewerImage(previousArivisViewerImage: string | FileModel | undefined) {
        this._previousArivisViewerImage = previousArivisViewerImage;
    }

    get rerun(): boolean {
        return this._rerun;
    }

    set rerun(rerun: boolean) {
        this._rerun = rerun;
    }

    storeWorkflow(workflowReadModel: WorkflowV1Run, replaceOriginal: boolean = false): void {
        this._workflow = convertToWorkflow(workflowReadModel);
        if (!this._originalWorkflowJSON || replaceOriginal) {
            this.setCurrentWorkflowAsOriginal();
        }
    }

    clearState(): void {
        this._originalWorkflowJSON = undefined;
        this._workflow = null;
        this._systemModules = null;
        this._allVisibleModules = null;
        this._experiments = null;
        this._allModuleCompliance = null;
        this._allModuleCategories = null;
        this._isPublicWorkflowUpdateByAdmin = false;
        this._runSearchObject = {};
        this._previousArivisViewerImage = undefined;
        this._rerun = false;
        this._initialFiles = [];
    }

    clearFilesToChooseFromSort(flowId: string): void {
        this._runSearchObject[flowId].filesToChooseFromSearchOptions = undefined;
    }

    clearSelectedFilesSearchOptions(flowId: string): void {
        this._runSearchObject[flowId].selectedFilesCurrentSearchOptions = undefined;
    }

    makeStateConsistent(id?: string | null): void {
        if (!id || (this._workflow && id !== this._workflow.id)) {
            this.clearState();
        }
    }

    hasWorkflowChanged(): boolean {
        if (!this._workflow) {
            return false;
        }
        const currentWorkflowJSON: string | undefined = this.prepareWorkflowToCompare(this._workflow!);
        const originalWorkflow: Workflow = JSON.parse(this._originalWorkflowJSON!);
        const currentWorkflow: Workflow = JSON.parse(currentWorkflowJSON!);
        if (deepEqualInAnyOrder(originalWorkflow, currentWorkflow, false, undefined, ['_value'])) {
            return false;
        }
        return true;
    }

    private setCurrentWorkflowAsOriginal(): void {
        this._originalWorkflowJSON = this.prepareWorkflowToCompare(this._workflow!);
    }

    private prepareWorkflowToCompare(workflow: Workflow): string | undefined {
        // some properties are created for workflow object for UI purposes and only frontend uses them,
        // they are calculated based on other workflow properties so we do not have to use them during equality check
        return stableStringify(workflow, { skip: ['stepError', 'usedModules', 'usedModuleVersions', 'run'] });
    }
}
